// Universal CommentsPanel (N1) — list + composer with @mention autocomplete + reply threading.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, TextField, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { SectionCard, Icon } from "../../mui";
import {
  createMessageApi,
  listAdminProfiles,
  listMessagesApi,
  type AdminProfile,
  type BrainMessage,
} from "../../lib/brain-api";
import { useProfile } from "../../lib/profile";
import { relDate } from "../../mui/format";

const c = brainTokens.color;

function handleFor(p: AdminProfile): string {
  return (p.personSlug || p.email?.split("@")[0] || p.userId).toLowerCase();
}

function authorLabel(author: string, profiles: AdminProfile[]): string {
  const p = profiles.find((x) => x.userId === author);
  return p?.displayName || p?.personSlug || author.slice(0, 8);
}

export function CommentsPanel({
  source,
  sourceId,
  label = "Comments",
  bare = false,
}: {
  source: string;
  sourceId: string;
  label?: string;
  /** Render flat (no SectionCard) — for hosts with their own chrome, e.g. the dock. */
  bare?: boolean;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useProfile();
  const [messages, setMessages] = useState<BrainMessage[]>([]);
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState<BrainMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mentionQ, setMentionQ] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const load = useCallback(async () => {
    if (!source || !sourceId) return;
    setLoading(true);
    try {
      const [msgs, profs] = await Promise.all([
        listMessagesApi(source, sourceId),
        listAdminProfiles().catch(() => [] as AdminProfile[]),
      ]);
      setMessages(msgs);
      setProfiles(profs.filter((p) => p.active !== false));
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to load comments", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [source, sourceId, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const mentionHits = useMemo(() => {
    if (mentionQ == null) return [];
    const q = mentionQ.toLowerCase();
    return profiles
      .filter((p) => {
        const h = handleFor(p);
        const n = (p.displayName || "").toLowerCase();
        return !q || h.includes(q) || n.includes(q);
      })
      .slice(0, 6);
  }, [mentionQ, profiles]);

  const onBodyChange = (value: string) => {
    setBody(value);
    const caret = value; // simple: match trailing @token
    const m = /(?:^|\s)@([a-zA-Z0-9._-]*)$/.exec(caret);
    setMentionQ(m ? m[1] : null);
  };

  const insertMention = (p: AdminProfile) => {
    const handle = handleFor(p);
    setBody((prev) => prev.replace(/@([a-zA-Z0-9._-]*)$/, `@${handle} `));
    setMentionQ(null);
    inputRef.current?.focus();
  };

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const mentions = [...text.matchAll(/@([a-zA-Z0-9._-]+)/g)].map((m) => m[1].toLowerCase());
      await createMessageApi({
        source,
        sourceId,
        body: text,
        mentions,
        replyTo: replyTo?.id,
      });
      setBody("");
      setReplyTo(null);
      setMentionQ(null);
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to post", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  const ids = new Set(messages.map((m) => m.id));
  // Roots = top-level messages OR orphaned replies whose parent isn't in the set,
  // so a reply is never silently dropped when its parent is resolved/paginated away.
  const roots = messages.filter((m) => !m.replyTo || !ids.has(m.replyTo));
  const repliesOf = (id: string) => messages.filter((m) => m.replyTo === id && m.replyTo !== m.id);

  const renderRow = (m: BrainMessage, depth = 0, seen: Set<string> = new Set()) => {
    if (seen.has(m.id)) return null; // guard against malformed replyTo cycles
    const next = new Set(seen).add(m.id);
    // System events (created, went stale, Jira status…) interleave as compact
    // timeline lines — not authored comments, no reply affordance.
    if (m.kind === "system") {
      return (
        <Box key={m.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75, color: c.muted }}>
          <Icon name="activity" size={13} />
          <Box sx={{ fontSize: 12.5, flex: 1 }}>{m.body}</Box>
          <Box sx={{ fontSize: 11, color: c.faint, fontFamily: brainTokens.font.mono }}>{relDate(m.createdAt)}</Box>
        </Box>
      );
    }
    return (
    <Box key={m.id} sx={{ ml: depth ? 3 : 0, py: 1.25, borderTop: depth ? "none" : `1px solid ${c.borderLight}` }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
        <Box sx={{ fontSize: 13, fontWeight: 600, color: c.ink2 }}>{authorLabel(m.author, profiles)}</Box>
        <Box sx={{ fontSize: 11, color: c.faint, fontFamily: brainTokens.font.mono }}>{relDate(m.createdAt)}</Box>
        {m.author === profile?.userId ? null : (
          <Button size="small" sx={{ minWidth: 0, px: 0.75, fontSize: 11 }} onClick={() => { setReplyTo(m); inputRef.current?.focus(); }}>
            Reply
          </Button>
        )}
      </Box>
      <Box sx={{ fontSize: 13.5, color: c.ink3, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{m.body}</Box>
      {repliesOf(m.id).map((r) => renderRow(r, depth + 1, next))}
    </Box>
    );
  };

  const content = (
    <>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : messages.length === 0 ? (
        <Box sx={{ fontSize: 13, color: c.muted2, mb: 1.5 }}>No comments yet — start the thread.</Box>
      ) : (
        <Box sx={{ mb: 1.5, "& > div:first-of-type": { borderTop: "none" } }}>{roots.map((m) => renderRow(m))}</Box>
      )}

      {replyTo && (
        <Box sx={{ fontSize: 12, color: c.muted, mb: 1, display: "flex", gap: 1, alignItems: "center" }}>
          Replying to {authorLabel(replyTo.author, profiles)}
          <Button size="small" onClick={() => setReplyTo(null)} sx={{ minWidth: 0, px: 0.5 }}>cancel</Button>
        </Box>
      )}

      <Box sx={{ position: "relative" }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          size="small"
          placeholder="Write a comment… use @ to mention"
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {mentionHits.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "100%",
              mb: 0.5,
              bgcolor: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              zIndex: 2,
              overflow: "hidden",
            }}
          >
            {mentionHits.map((p) => (
              <Box
                key={p.userId}
                component="button"
                type="button"
                onClick={() => insertMention(p)}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: 0,
                  background: "transparent",
                  px: 1.5,
                  py: 1,
                  cursor: "pointer",
                  fontSize: 13,
                  color: c.ink,
                  "&:hover": { bgcolor: c.tealTint },
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>@{handleFor(p)}</Box>
                <Box component="span" sx={{ color: c.muted, ml: 1 }}>{p.displayName}</Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <Button
          variant="contained"
          size="small"
          disabled={sending || !body.trim()}
          onClick={() => void send()}
          startIcon={<Icon name="send" size={14} />}
        >
          Comment
        </Button>
      </Box>
    </>
  );

  if (bare) return content;
  return <SectionCard label={label} headerMb={12}>{content}</SectionCard>;
}
